import { currentMilestone, CUSTOM_AVATAR_MILESTONES, sortedMilestones } from './milestones';
import { FrameTier, MilestoneKey, StreakMilestone, StreakSnapshot } from './types';

/**
 * Change Avatar's streak section (EN-11164, canvas board 8 and D4): one row
 * per milestone the server sends, each with its status avatars from the pool
 * manifest. Pure, so the copy and the locks are tested without rendering.
 *
 * Unlocks keep: a status's avatars stay selectable once its day was reached,
 * even after the streak ends ("Avatars you unlock stay yours for good"). The
 * chip, the bar and the frame follow the current streak.
 */

export interface LadderTile {
  /** The avatar id the profile stores (the pool file name); empty for the custom tile. */
  file: string;
  name: string;
  locked: boolean;
  /** The Legend request tile: never selectable. */
  custom: boolean;
  /** Accessible name: "Compass, locked, unlocks at day 30". */
  label: string;
  /** The message a tap on a locked tile shows. */
  lockedMessage: string;
}

export type ChipTone = 'held' | 'next' | 'later';

export interface LadderRow {
  key: MilestoneKey;
  name: string;
  days: number;
  held: boolean;
  tone: ChipTone;
  chip: string;
  /** Only the next status: the bar's fill, its line and its accessible name. */
  bar: { percent: number; line: string; label: string } | null;
  tiles: LadderTile[];
  note: string | null;
  /** The radiogroup's accessible name, with the chip. */
  label: string;
}

export interface AvatarLadder {
  rows: LadderRow[];
  /** "Day 17 · Grinder status", "Day 2", "No streak yet". */
  dayLine: string;
  /** The status the current streak holds (frame tier), if any. */
  current: StreakMilestone | null;
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A pool file's display name, its subject: `cowboy-5-lasso.png` → "Lasso",
 * `001-saloon.png` → "Saloon", `004-poker+cards.png` → "Poker cards".
 */
export function avatarName(file: string): string {
  const base = file.replace(/\.[a-z0-9]+$/i, '');
  // The older pool files use `+` for a space (`004-poker+cards.png`).
  const parts = base.replace(/\+/g, ' ').split('-').filter(Boolean);
  // Drop the plain index (`001`) or the set key and index (`cowboy-5`).
  if (parts.length > 1 && /^\d+$/.test(parts[0])) parts.shift();
  else if (parts.length > 2 && /^\d+$/.test(parts[1])) parts.splice(0, 2);
  return titleCase(parts.join(' ')) || 'Avatar';
}

const days = (n: number, word = 'day') => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The furthest day the player has unlocked: the best run, or the highest status the server recorded. */
export function unlockedThrough(snap: StreakSnapshot): number {
  const highest = snap.milestones.find((m) => m.key === snap.highestMilestoneKey);
  return Math.max(snap.bestStreak ?? 0, snap.currentStreak ?? 0, highest?.days ?? 0);
}

/** The streak the frame, chips and bar read: none once it ended. */
export function liveDays(snap: StreakSnapshot): number {
  return snap.status === 'ended' || snap.status === 'none' ? 0 : snap.currentStreak;
}

export function buildAvatarLadder(
  snap: StreakSnapshot,
  sets: Record<string, string[]>
): AvatarLadder {
  const milestones = sortedMilestones(snap.milestones);
  const current = liveDays(snap);
  const unlocked = unlockedThrough(snap);
  // The first status not yet unlocked; its bar counts the current streak.
  const next = milestones.find((m) => m.days > unlocked) ?? null;
  const holding = currentMilestone(milestones, current) ?? null;

  const rows = milestones.map((m): LadderRow => {
    const held = m.days <= unlocked;
    const isNext = next?.key === m.key;
    const toGo = Math.max(0, m.days - current);
    const chip = held
      ? `Unlocked · day ${m.days}`
      : isNext
      ? `${days(toGo)} to go`
      : `Unlocks at day ${m.days}`;
    const moreDays = toGo ? ` ${days(toGo, 'more day')}.` : '';
    const files = (m.avatarSetKey && sets[m.avatarSetKey]) || [];
    const tiles: LadderTile[] = files.map((file) => {
      const name = avatarName(file);
      return {
        file,
        name,
        locked: !held,
        custom: false,
        label: held ? name : `${name}, locked, unlocks at day ${m.days}`,
        lockedMessage: `Reach ${m.name} status (day ${m.days}) to unlock ${name}.${moreDays}`
      };
    });
    const custom = CUSTOM_AVATAR_MILESTONES.has(m.key);
    if (custom) {
      tiles.push({
        file: '',
        name: 'Custom avatar, made for you',
        locked: !held,
        custom: true,
        label: held
          ? 'Custom avatar, made for you'
          : `Custom avatar, locked, request it at day ${m.days}`,
        lockedMessage: held
          ? 'Your custom avatar: tell support what you want and it is made for you.'
          : `Reach ${m.name} status (day ${m.days}) to request your custom avatar.${moreDays}`
      });
    }
    return {
      key: m.key,
      name: m.name,
      days: m.days,
      held,
      tone: held ? 'held' : isNext ? 'next' : 'later',
      chip,
      bar: isNext
        ? {
            percent: Math.round(Math.min(100, (current / m.days) * 100)),
            line: `Day ${current} of ${m.days} · ${toGo} to go`,
            label: `Day ${current} of ${m.days} toward ${m.name} status`
          }
        : null,
      tiles,
      note: custom
        ? held
          ? 'Plus one custom avatar, yours to request. Support will reach out.'
          : `Plus one custom avatar you request at day ${m.days}. Nobody else gets the same one.`
        : null,
      label: `${m.name} status avatars, ${chip.toLowerCase()}`
    };
  });

  const dayLine =
    current === 0 ? 'No streak yet' : `Day ${current}${holding ? ` · ${holding.name} status` : ''}`;
  return { rows, dayLine, current: holding };
}

/** Every avatar file a status the player unlocked grants. */
export function unlockedStatusFiles(ladder: AvatarLadder): Set<string> {
  const out = new Set<string>();
  ladder.rows.forEach((r) => r.tiles.forEach((t) => !t.locked && !t.custom && out.add(t.file)));
  return out;
}

/** The switch card's second line (one line on the phone in every state). */
export function frameSwitchLine(
  holding: StreakMilestone | null,
  on: boolean,
  first?: StreakMilestone
): string {
  // The switch works with no status too (JRam 11/03): it is the setting the
  // frame will follow once one is reached.
  if (!holding && !on) return 'Hidden. Nobody will see your status.';
  if (!holding)
    return first
      ? `Shows at ${first.name} status (day ${first.days})`
      : 'Shows at your first status';
  return on ? 'Frame visible for you and your opponents' : 'Hidden. Nobody sees your status.';
}

/** The Selected card's frame line. */
export function selectedFrameLine(holding: StreakMilestone | null, on: boolean): string {
  if (!holding) return 'No frame yet';
  return on ? `${holding.name} frame` : 'Frame hidden';
}

/** The tier the tiles and the Selected card wear while the picker is open. */
export function wornTier(holding: StreakMilestone | null, on: boolean): FrameTier | null {
  return holding && on ? holding.frameKey : null;
}
